import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Users, Clock, Target, Map } from "lucide-react";

const SERVICES = [
  {
    icon: Users,
    title: "Permanent Search",
    blurb: "Retained and contingent search for engineering, product, design, AI, data and platform leaders. End-to-end ownership with market mapping, structured assessment and offer negotiation.",
    bullets: ["Engineers: IC3 → Staff/Principal", "Leaders: EM, Director, VP, CTO", "5-week average time-to-hire"],
  },
  {
    icon: Clock,
    title: "Contract & Interim",
    blurb: "Rapid-turnaround placements of vetted senior contractors, day-rate or statement-of-work. Compliant in the UK and across EU jurisdictions.",
    bullets: ["Daily rate + SOW options", "IR35, AUG, ZZP & local compliance", "Start dates within 2-4 weeks"],
  },
  {
    icon: Target,
    title: "Executive Search",
    blurb: "Confidential, retained search for C-suite and functional leaders. Named-account mapping, multi-stage assessment, onboarding partnership.",
    bullets: ["Retained only", "Named-list mapping", "Discreet market entry"],
  },
  {
    icon: Map,
    title: "Talent Mapping & Advisory",
    blurb: "Know your market before you hire. We deliver anonymised talent maps, salary benchmarking and org-design advice.",
    bullets: ["Competitor org charts", "Compensation data", "Diversity benchmarking"],
  },
];

const PROCESS = [
  { title: "Discovery", text: "60-minute working session with the hiring team to define role, leveling and success metrics." },
  { title: "Market Map", text: "We build a named list of qualified candidates within 72 hours and agree an outreach plan." },
  { title: "Shortlist", text: "Structured assessment and deeply briefed candidates, typically 3-5 in the first week." },
  { title: "Offer & Close", text: "We manage expectations, references and onboarding so placements start and stick." },
];

export default function Services() {
  return (
    <div data-testid="services-page" className="pt-28 md:pt-36">
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16">
        <h1 className="font-display font-black text-5xl md:text-7xl tracking-tighter text-white leading-[0.92] max-w-5xl">
          Four ways we help you build your team.
        </h1>
        <p className="mt-10 text-white/70 text-lg font-body leading-relaxed max-w-3xl">
          Whether you're making your first senior hire or scaling a 50-strong engineering organisation across the UK, Europe or the US, we offer a service model to match. Everything is led by senior recruiters, no pass-offs.
        </p>
      </section>

      <section className="border-y border-white/10">
        <div className="max-w-7xl mx-auto px-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={s.title} className={`p-10 md:p-14 border-white/10 ${i % 2 === 0 ? "md:border-r" : ""} ${i < 2 ? "border-b md:border-b" : ""}`} data-testid={`service-${s.title.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="w-12 h-12 border border-white/20 flex items-center justify-center text-[#60D8FF] mb-8">
                    <Icon size={22} />
                  </div>
                  <h3 className="font-display font-bold text-3xl md:text-4xl tracking-tighter text-white mb-4">{s.title}</h3>
                  <p className="text-white/70 font-body leading-relaxed mb-6">{s.blurb}</p>
                  <ul className="space-y-2">
                    {s.bullets.map((b) => (
                      <li key={b} className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/50 flex items-center gap-2">
                        <span className="text-[#3B82F6]">·</span>{b}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#0a0a0a] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-12 mb-16">
            <div className="md:col-span-4">
              <div className="font-mono text-[10px] text-[#3B82F6] uppercase tracking-[0.3em] mb-4">Our process</div>
            </div>
            <div className="md:col-span-8">
              <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter text-white leading-[0.95]">From brief to offer. Typically four weeks.</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-0 border border-white/10">
            {PROCESS.map((p, i) => (
              <div key={p.title} className={`p-8 md:p-10 ${i !== 3 ? "md:border-r border-white/10" : ""} ${i < 2 ? "border-b md:border-b-0 border-white/10" : ""} ${i === 2 ? "border-b md:border-b-0 border-white/10" : ""}`}>
                <ArrowRight size={28} className="text-[#3B82F6] mb-4" strokeWidth={2.5} />
                <div className="font-display font-bold text-xl text-white tracking-tight mb-2">{p.title}</div>
                <p className="text-white/60 text-sm font-body leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter text-white leading-[0.95]">Hiring across AI, Data, Software or Product?</h2>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/contact" data-testid="services-cta-contact" className="inline-flex items-center gap-2 btn-volt px-7 py-4 text-xs uppercase tracking-[0.2em]">
              Start a brief <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
